<template>
  <v-container fluid>
    <v-row>
      <v-col>
        <v-form
          ref="form"
          v-model="isFormValid"
        >
          <v-container fluid>
            <v-row>
              <v-col
                cols="12"
                md="7"
              >
                <p>Select or create parties that should be part of this case</p>
                <v-col cols="12">
                  <v-btn
                    v-if="!createRecord"
                    block
                    color="primary"
                    @click="_ => associateParty()"
                  >
                    Associate a party
                  </v-btn>
                  <v-autocomplete
                    v-else
                    v-model="selectedUnassociatedParties"
                    :rules="[rules.associated_parties]"
                    chips
                    deletable-chips
                    label="Associate a party"
                    multiple
                    outlined
                    return-object
                    :items="unassociatedPartiesWithIdentifier"
                  />
                </v-col>
                <v-col cols="12">
                  <v-row
                    align="center"
                    no-gutters
                  >
                    <v-divider />
                    <span class="mx-4 text-caption text-uppercase">
                      OR
                    </span>
                    <v-divider />
                  </v-row>
                </v-col>
                <v-col cols="12">
                  <v-btn
                    color="secondary"
                    block
                    @click="_ => addCreatedParty()"
                  >
                    Create a new party
                  </v-btn>
                </v-col>
                <v-col
                  v-if="isOWRImportPartyEnabled"
                  cols="12"
                >
                  <v-btn
                    color="secondary"
                    block
                    @click="_ => addImportedParty()"
                  >
                    Import Party from Oregon Worker Relief Fund
                  </v-btn>
                </v-col>
              </v-col>
              <v-col
                cols="12"
                md="5"
              >
                <v-list-item
                  v-for="party of edited.parties"
                  :key="party.identifier"
                >
                  <v-list-item-avatar>
                    <v-icon
                      small
                      class="grey lighten-1"
                      dark
                    >
                      mdi-account
                    </v-icon>
                  </v-list-item-avatar>

                  <v-list-item-content>
                    <v-list-item-title>{{ party.first_name }} {{ party.middle_name }} {{ party.last_name }}</v-list-item-title>
                    <v-list-item-subtitle>{{ dobText(party) }} {{ genderText(party.gender_id) }} {{ party.a_number && `- ${party.a_number}` }}</v-list-item-subtitle>
                  </v-list-item-content>

                  <v-list-item-action>
                    <v-btn
                      v-if="!party.id"
                      icon
                      @click="_ => editCreatedParty(party)"
                    >
                      <v-icon>
                        mdi-pencil-outline
                      </v-icon>
                    </v-btn>
                    <v-btn
                      v-else
                      icon
                      @click="_ => editParty(party)"
                    >
                      <v-icon>
                        mdi-pencil-outline
                      </v-icon>
                    </v-btn>
                  </v-list-item-action>

                  <v-list-item-action>
                    <v-btn
                      color="red"
                      icon
                      @click="_ => removeParty(party.identifier)"
                    >
                      <v-icon>
                        mdi-delete-outline
                      </v-icon>
                    </v-btn>
                  </v-list-item-action>
                </v-list-item>
              </v-col>
            </v-row>
          </v-container>
        </v-form>
      </v-col>
    </v-row>

    <PartyFieldsDialog
      v-if="showPartyFieldsDialog"
      v-model="showPartyFieldsDialog"
      :party="selectedParty"
      @save="party => saveCreatedParty(party)"
    />

    <AssociateParty
      :callback="onConfirmAssociateParties"
      :is-create="!edited.id"
    />

    <ImportPartyDialog
      v-if="showExternalPartyImportDialog"
      v-model="showExternalPartyImportDialog"
      @save="party => saveCreatedParty(party)"
    />

    <ImportedPartyEditorDialog
      v-if="showExternalPartyEditorDialog"
      v-model="showExternalPartyEditorDialog"
      :party="selectedParty"
      @save="party => saveCreatedParty(party)"
    />
  </v-container>
</template>

<script>
import { isEmpty, uniqueId } from 'lodash'
import { mapActions, mapGetters } from 'vuex'
import Vue from 'vue'

import { alertHandler } from '@/alertHandler'
import { dateDisplayFormat } from '@/utilities'
import { rules } from '@/utilities/validation-rules'

import { LEGAL_CASE_NAMESPACE } from '@/store/legal-case'
import { PARTY_NAMESPACE } from '@/store/party'
import { SHARED_NAMESPACE } from '@/store/shared'

import AssociateParty from '@/components/Dialogs/LegalCase/AssociateParty.vue'
import ImportPartyDialog from './LegalCaseEditorImportPartyDialog.vue'
import ImportedPartyEditorDialog from './LegalCaseEditorImportedPartyEditorDialog.vue'
import PartyFieldsDialog from './LegalCaseEditorPartyFieldsDialog.vue'

export default {
  name: 'LegalCaseEditorParties',

  components: {
    AssociateParty,
    ImportPartyDialog,
    ImportedPartyEditorDialog,
    PartyFieldsDialog,
  },

  props: {
    createRecord: {
      type: Boolean,
      default: false,
    },

    valid: {
      type: Boolean,
      default: false,
    },
  },

  setup () {
    return {
      ...alertHandler(),
    }
  },

  data () {
    return {
      createdParties: [],
      rules: {
        ...rules,
        associated_parties: rules.required,
      },

      selectedParty: {},
      selectedUnassociatedParties: [],
      showExternalPartyEditorDialog: false,
      showExternalPartyImportDialog: false,
      showPartyFieldsDialog: false,
    }
  },

  computed: {
    compoundProperty () {
      return [this.selectedUnassociatedParties, this.createdParties]
    },

    isFormValid: {
      get () { return this.valid },
      set (val) { this.$emit('update:valid', val) },
    },

    isOWRImportPartyEnabled () {
      return this.currentUser['owr_api_configured?'] && this.features?.owr?.enabled
    },

    unassociatedPartiesWithIdentifier () {
      return this.unassociatedParties().map(p => ({ ...p, identifier: uniqueId() }))
    },

    ...mapGetters([
      'currentUser',
      'genders',
    ]),

    ...mapGetters(LEGAL_CASE_NAMESPACE, [
      'edited',
    ]),

    ...mapGetters(PARTY_NAMESPACE, [
      'peParty',
      'unassociatedParties',
    ]),

    ...mapGetters(SHARED_NAMESPACE, [
      'features',
    ]),
  },

  watch: {
    compoundProperty () {
      this.setValidationRules()
      this.$nextTick().then(this.validate)

      Vue.set(this.edited, 'parties', [
        ...this.selectedUnassociatedParties,
        ...this.createdParties,
      ])
    },

    'edited.parties' (value) {
      if (value.length > 0) return
      if (this.selectedUnassociatedParties.length > 0) this.selectedUnassociatedParties = []
    },

    peParty: {
      deep: true,

      handler (value) {
        const editedPartyIndex = this.edited.parties.findIndex(p => p.id === this.peParty.id)
        this.edited.parties.splice(editedPartyIndex, 1, { ...value, identifier: uniqueId() })
      },
    },
  },

  created () {
    this.setValidationRules()
    this.fetchParties()
    this.getGendersModel()
  },

  methods: {
    addCreatedParty () {
      this.showPartyFieldsDialog = true
    },

    addImportedParty () {
      this.showExternalPartyImportDialog = true
    },

    associateParty () {
      this.setShowAssociatePartyDialog(true)
    },

    createCreatedParty (party) {
      this.createdParties.push({
        ...party,
        identifier: uniqueId(),
      })
    },

    dobText (party) {
      let text = party.dob
      if (party.uncertain_dob) text = 'Uncertain DOB'
      else if (text === undefined) return
      else text = this.formatDate(text)

      return `DOB: ${text}`
    },

    editCreatedParty (party) {
      this.selectedParty = party

      if (party.source === 'owr') {
        this.showExternalPartyEditorDialog = true
      } else {
        this.showPartyFieldsDialog = true
      }
    },

    editParty (party) {
      this.peSharedQESliderOpen(party.id)
    },

    formatDate (unformattedDate) {
      return !isEmpty(unformattedDate) ? dateDisplayFormat(unformattedDate) : ''
    },

    genderText (genderId) {
      if (!genderId) return

      const gender = this.genders?.find(gender => gender.id === genderId)
      if (!gender) return

      return `Gender: ${gender.name}`
    },

    newCreatedParty () {
      this.showPartyFieldsDialog = true
    },

    onConfirmAssociateParties (selectedParties) {
      this.selectedUnassociatedParties = selectedParties.map(p => ({ ...p, identifier: uniqueId() }))
    },

    partyMissing () {
      return (
        (
          this.selectedUnassociatedParties === null ||
            this.selectedUnassociatedParties.length === 0
        )
      )
    },

    removeParty (identifier) {
      const createdPartyIndex = this.createdParties.findIndex(p => p.identifier === identifier)
      if (createdPartyIndex > -1) this.createdParties.splice(createdPartyIndex, 1)

      const selectedUnassociatedPartyIndex = this.
        selectedUnassociatedParties.
        findIndex(p => p.identifier === identifier)

      if (selectedUnassociatedPartyIndex === -1) return

      this.selectedUnassociatedParties.splice(selectedUnassociatedPartyIndex, 1)
    },

    saveCreatedParty (party) {
      if (party.identifier) this.updateCreatedParty(party)
      else this.createCreatedParty(party)

      this.selectedParty = {}
    },

    setValidationRules () {
      this.rules.associated_parties = rules.required
      if (this.createdParties.length > 0) this.rules.associated_parties = _ => true
    },

    updateCreatedParty (party) {
      const createdPartyIndex = this.
        createdParties.
        findIndex(createdParty => createdParty.identifier === party.identifier)

      if (createdPartyIndex < 0) this.createdParties.push(party)
      else this.createdParties.splice(createdPartyIndex, 1, party)
    },

    validate () {
      if (this.edited.parties.length === 0) {
        this.isFormValid = false
        return
      } else this.isFormValid = true

      this.$refs.form.validate()
    },

    ...mapActions([
      'getGendersModel',
    ]),

    ...mapActions(LEGAL_CASE_NAMESPACE, [
      'setShowAssociatePartyDialog',
    ]),

    ...mapActions(PARTY_NAMESPACE, [
      'fetchParties',
      'peSharedQESliderOpen',
    ]),

  },
}
</script>
